import { cn } from "@/lib/utils";
import { type LucideIcon } from "lucide-react";
import { type ReactNode } from "react";

export type PillTone =
  | "emerald"
  | "sky"
  | "amber"
  | "indigo"
  | "fuchsia"
  | "slate";

const TONE_CLASSES: Record<PillTone, string> = {
  emerald:
    "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-inset ring-emerald-500/20",
  sky: "bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-1 ring-inset ring-sky-500/20",
  amber:
    "bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-500/20",
  indigo:
    "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 ring-1 ring-inset ring-indigo-500/20",
  fuchsia:
    "bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300 ring-1 ring-inset ring-fuchsia-500/20",
  slate:
    "bg-slate-500/10 text-slate-700 dark:text-slate-300 ring-1 ring-inset ring-slate-500/20",
};

type TonePillProps = {
  tone: PillTone;
  icon?: LucideIcon;
  children: ReactNode;
  className?: string;
};

/**
 * Rounded pill badge in a themed color. Used for market classification
 * (B2B/B2C, ownership status) and any binary-state marker where a colored
 * accent pill conveys more than a neutral Badge would.
 */
export default function TonePill({
  tone,
  icon: Icon,
  children,
  className,
}: TonePillProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold tracking-wide",
        TONE_CLASSES[tone],
        className,
      )}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </span>
  );
}
